import {
  memberDetailsSchema,
  type MemberDetails,
  type MembershipFeePlan,
} from '@growup/accounts-shared';
import { lisbonDate } from '@growup/arena-shared';
import { Button, Card, DateInput, Field, Input, Notice, Select } from '@growup/ui';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { MemberTeamsCard } from '../teams/MemberTeamsCard';
import {
  APPLICATION_STATUS_LABEL,
  FEE_PLAN_LABEL,
  FEE_SOURCE_LABEL,
  formatDay,
  formatEuros,
  ORIGIN_LABEL,
} from './labels';
import { LevelBadge } from './LevelBadge';
import { MemberCardView } from './MemberCardView';
import { PublicPageCard } from './PublicPageCard';
import { ProfileFields, ProfileForm } from './Profile';
import { StreamerCard } from './StreamerCard';
import { StreamerShirtCard } from './StreamerShirt';

/** A member's record: data (by the minors rule), level, fees and guardian links. */
export function MemberDetail({
  accountId,
  canManage,
  canSeeStreamerNumbers = false,
  onOpen,
  onOpenStreamer,
  onChanged,
}: {
  accountId: string;
  canManage: boolean;
  /** `streaming.manage`: o painel do streamer aparece na ficha. */
  canSeeStreamerNumbers?: boolean;
  onOpen: (accountId: string) => void;
  onOpenStreamer?: ((accountId: string) => void) | undefined;
  onChanged: () => void;
}) {
  const [details, setDetails] = useState<MemberDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    getJson(`/api/members/members/${accountId}/details`, memberDetailsSchema)
      .then((d) => {
        setError(null);
        setDetails(d);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [accountId]);

  useEffect(load, [load]);

  const changed = () => {
    load();
    onChanged();
  };

  if (!details) return error ? <Notice tone="danger">{error}</Notice> : null;
  const { member } = details;

  return (
    <div className="flex flex-col gap-4">
      <Card
        headingLevel={2}
        ruled
        eyebrow={`Login ${member.login}`}
        title={member.profile?.fullName ?? member.login}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gu-ink-muted">
          <LevelBadge level={member.level} />
          <span>Origem: {ORIGIN_LABEL[member.origin]}</span>
          {member.memberSince ? (
            <span>
              · {member.level === 'user' ? 'Foi membro desde' : 'Membro desde'}{' '}
              {formatDay(lisbonDate(new Date(member.memberSince)))}
            </span>
          ) : null}
          {member.associateUntil ? (
            <span>· Quota até {formatDay(member.associateUntil)}</span>
          ) : null}
          {member.isMinor ? <span>· Menor de idade</span> : null}
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {member.redacted ? (
          <Notice tone="info" title="Dados pessoais reservados">
            É menor de idade e só os administradores veem os dados.
          </Notice>
        ) : member.profile === null ? (
          <p className="text-gu-ink-muted">
            Ainda sem ficha de membro (nome, data de nascimento, contactos, CC e morada).
          </p>
        ) : editing ? (
          <ProfileForm
            accountId={member.accountId}
            profile={member.profile}
            onDone={(saved) => {
              setEditing(false);
              if (saved) changed();
            }}
          />
        ) : (
          <>
            <ProfileFields profile={member.profile} />
            {canManage ? (
              <Button
                variant="secondary"
                size="sm"
                className="w-fit"
                onClick={() => setEditing(true)}
              >
                Corrigir dados
              </Button>
            ) : null}
          </>
        )}
      </Card>

      <MemberCardView accountId={member.accountId} canManage={canManage} />

      <PublicPageCard accountId={member.accountId} login={member.login} canManage={canManage} />

      <MemberTeamsCard accountId={member.accountId} />

      <StreamerCard
        accountId={member.accountId}
        canSeeNumbers={canSeeStreamerNumbers}
        onOpenStreamer={onOpenStreamer}
      />

      {canSeeStreamerNumbers ? (
        <StreamerShirtCard accountId={member.accountId} onChanged={changed} />
      ) : null}

      {canManage ? <ManageCard details={details} onChanged={changed} /> : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card headingLevel={2} title="Pagamentos da quota">
          {details.monthlyPaymentsThisYear > 0 ? (
            <p className="mb-2 text-body">
              Mensal: {details.monthlyPaymentsThisYear}{' '}
              {details.monthlyPaymentsThisYear === 1 ? 'mês pago' : 'meses pagos'} este ano.
            </p>
          ) : null}
          <List empty="Nenhuma quota registada.">
            {details.fees.map((f) => (
              <li key={`${f.validFrom}-${f.recordedAt}`}>
                {formatDateTime(f.recordedAt)} · {FEE_PLAN_LABEL[f.plan]} ·{' '}
                {formatEuros(f.amountCents)} · {FEE_SOURCE_LABEL[f.source]}
                <span className="block text-caption text-gu-ink-muted">
                  Associado de {formatDay(f.validFrom)} a {formatDay(f.validUntil)}
                </span>
              </li>
            ))}
          </List>
        </Card>
        <Card headingLevel={2} title="Pedidos de membro">
          <List empty="Nenhum pedido.">
            {details.applications.map((a) => (
              <li key={a.id}>
                {formatDateTime(a.submittedAt)} · {APPLICATION_STATUS_LABEL[a.status]}
                {a.decisionNote ? ` · ${a.decisionNote}` : ''}
              </li>
            ))}
          </List>
        </Card>
      </div>

      {details.guardians.length > 0 || details.youths.length > 0 ? (
        <Card headingLevel={2} title="Encarregados de educação">
          <List empty="">
            {details.guardians.map((g) => (
              <li key={g.accountId}>
                Encarregado: <LinkButton onClick={() => onOpen(g.accountId)}>{g.login}</LinkButton>{' '}
                ·{' '}
                {g.authorizationActive
                  ? `✓ autorizou a Arena${g.authorizedSince ? ` a ${formatDateTime(g.authorizedSince)}` : ''}`
                  : '! sem autorização da Arena'}
              </li>
            ))}
            {details.youths.map((y) => (
              <li key={y.accountId}>
                Encarregado de{' '}
                <LinkButton onClick={() => onOpen(y.accountId)}>{y.login}</LinkButton> ·{' '}
                {y.authorizationActive ? '✓ Arena autorizada' : '! Arena não autorizada'}
              </li>
            ))}
          </List>
        </Card>
      ) : null}
    </div>
  );
}

function List({ empty, children }: { empty: string; children: ReactNode[] }) {
  if (children.length === 0) return <p className="text-gu-ink-muted">{empty}</p>;
  return <ul className="flex flex-col gap-1">{children}</ul>;
}

function LinkButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      className="underline decoration-gu-accent decoration-2 underline-offset-4 hover:text-gu-accent"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Level by hand and fees by hand (`members.manage`), until the store handles fees. */
function ManageCard({ details, onChanged }: { details: MemberDetails; onChanged: () => void }) {
  const { member } = details;
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [paidOn, setPaidOn] = useState(() => lisbonDate(new Date()));
  const [euros, setEuros] = useState('30.00');
  const [plan, setPlan] = useState<MembershipFeePlan>('annual');
  const [since, setSince] = useState(() =>
    member.memberSince ? lisbonDate(new Date(member.memberSince)) : '',
  );

  const run = async (method: string, path: string, body: unknown, message: string) => {
    setError(null);
    setDone(null);
    try {
      await send(method, path, body);
      setDone(message);
      onChanged();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const base = `/api/members/members/${member.accountId}`;
  const isMember = member.level !== 'user';

  return (
    <Card headingLevel={2} title="Gestão">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {done ? <Notice tone="success">{done}</Notice> : null}
      <div className="flex flex-col gap-3">
        <p>
          {isMember
            ? 'É membro. Voltar a utilizador tira-lhe o acesso de membro (e à Arena).'
            : 'Ainda não é membro. Pode torná-lo membro sem pedido (por exemplo, inscrição ao balcão).'}
        </p>
        {isMember ? (
          <Button
            variant="danger"
            size="sm"
            className="w-fit"
            onClick={() => {
              if (window.confirm(`Voltar ${member.login} a utilizador?`)) {
                void run('PUT', `${base}/level`, { level: 'user' }, 'Voltou a utilizador.');
              }
            }}
          >
            Voltar a utilizador
          </Button>
        ) : (
          <Button
            size="sm"
            className="w-fit"
            onClick={() => void run('PUT', `${base}/level`, { level: 'member' }, 'É membro.')}
          >
            Tornar membro
          </Button>
        )}
      </div>
      {isMember ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Membro desde">
              <DateInput
                value={since}
                onChange={setSince}
                min="1990-01-01"
                max={lisbonDate(new Date())}
              />
            </Field>
            <Button
              variant="secondary"
              size="sm"
              className="mb-1.5"
              disabled={since === ''}
              onClick={() =>
                void run('PUT', `${base}/since`, { since }, 'Data de entrada corrigida.')
              }
            >
              Guardar data
            </Button>
          </div>
          <p className="text-caption text-gu-ink-muted">
            Dá os anos e a faixa da carta: Bronze aos 5 anos, Silver aos 10, Gold aos 15 e Platinum
            aos 20. Nos importados é quase sempre a data em que a conta foi criada no site antigo;
            corrija-a se a pessoa for membro há mais tempo.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Quota paga a">
              <DateInput value={paidOn} onChange={setPaidOn} />
            </Field>
            <Field label="Opção">
              <Select value={plan} onChange={(e) => setPlan(e.target.value as MembershipFeePlan)}>
                {(['annual', 'annual_jersey', 'monthly'] as const).map((p) => (
                  <option key={p} value={p}>
                    {FEE_PLAN_LABEL[p]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Valor (€)" className="w-28">
              <Input inputMode="decimal" value={euros} onChange={(e) => setEuros(e.target.value)} />
            </Field>
            <Button
              size="sm"
              className="mb-1.5"
              onClick={() => {
                const cents = Math.round(Number(euros.replace(',', '.')) * 100);
                if (!Number.isFinite(cents) || cents < 0) {
                  setError('Valor inválido.');
                  return;
                }
                void run(
                  'POST',
                  `${base}/fees`,
                  { paidOn, amountCents: cents, plan },
                  'Quota registada.',
                );
              }}
            >
              Registar quota
            </Button>
          </div>
          <p className="text-caption text-gu-ink-muted">
            Anual paga até 31/10 vale até ao fim do ano; a partir de 1/11, até ao fim do ano
            seguinte. Cada mês da mensal regista-se à parte e vale até ao fim do ano.
          </p>
        </>
      ) : null}
    </Card>
  );
}
